'use client'

import { useEffect } from 'react'
import { trackEvent, TrackEventName } from '@/utils/tracking/track'
import { KNOWN_CTA_NAMES, KNOWN_CTA_SECTIONS } from '@/features/landing-page/tracking/known-cta-names'
import { resolvePageType } from '@/features/landing-page/tracking/page-types'

/**
 * Delegated CTA capture for the marketing site (spec: marketing-cta-capture).
 *
 * One document-level click listener; any element carrying `data-cta` (or any descendant of
 * one) fires `cta_clicked`. Adding a tracked CTA to any marketing page is an attribute-only
 * change — no imports, no per-component tracking code. Never call trackEvent(CtaClicked)
 * manually; two firing paths is how double-counting starts.
 *
 * Derivation (not declaration — hand-set page/motion enums are what drifted before):
 * - section:     nearest ancestor `[data-section]`, else 'unknown'
 * - destination: the element's href, else 'anchor' (scroll/JS-handled buttons)
 * - cta_motion:  cal.com or demo-anchor destinations → 'demo'; everything else → 'self_serve'
 *
 * Uses the capture phase so CTAs that stopPropagation (or navigate immediately) still track.
 */

// cal.com hosts only — a fuzzy /demo/ match would misclassify self-serve links to pages that
// merely contain "demo" in the path.
const DEMO_DESTINATION_PATTERN = /(^|\/\/)(www\.)?cal\.com\//i

function deriveCta(el: Element) {
    const ctaName = el.getAttribute('data-cta') ?? 'unknown'
    const section = el.closest('[data-section]')?.getAttribute('data-section') ?? 'unknown'
    const href = el.getAttribute('href')
    const destination = href ?? 'anchor'

    const isDemo = ctaName === 'request_demo' || (href !== null && DEMO_DESTINATION_PATTERN.test(href))

    return {
        cta_name: ctaName,
        cta_motion: isDemo ? ('demo' as const) : ('self_serve' as const),
        page_type: resolvePageType(window.location.pathname),
        section,
        destination,
    }
}

export function LandingCtaTracking() {
    useEffect(() => {
        const onClick = (event: MouseEvent) => {
            if (!(event.target instanceof Element)) return
            const el = event.target.closest('[data-cta]')
            if (!el) return

            const properties = deriveCta(el)

            if (process.env.NODE_ENV === 'development') {
                if (!KNOWN_CTA_NAMES.has(properties.cta_name)) {
                    console.warn(
                        `[landing-cta-tracking] Unregistered cta_name "${properties.cta_name}" — captured anyway. ` +
                            'Add it to features/landing-page/tracking/known-cta-names.ts.'
                    )
                }
                // 'unknown' = no data-section ancestor, a legitimate state — only flag actual typos.
                if (properties.section !== 'unknown' && !KNOWN_CTA_SECTIONS.has(properties.section)) {
                    console.warn(
                        `[landing-cta-tracking] Unregistered data-section "${properties.section}" — captured anyway. ` +
                            'Add it to features/landing-page/tracking/known-cta-names.ts.'
                    )
                }
            }

            trackEvent(TrackEventName.CtaClicked, properties)
        }

        document.addEventListener('click', onClick, { capture: true })
        return () => document.removeEventListener('click', onClick, { capture: true })
    }, [])

    return null
}
